<script lang="ts" setup>
  import { type ToastRegistry, useDialogStack } from '@othercommerce/foundation';
  import { type PropType, useTemplateRef, watch, computed } from 'vue';

  const props = defineProps({
    toasts: { type: Array as PropType<ToastRegistry>, required: true },
  });

  const popover = useTemplateRef('popover');
  const stack = useDialogStack();

  const target = computed(() => {
    if (stack.top.value) {
      return '#' + stack.top.value;
    } else {
      return undefined;
    }
  });

  watch([() => props.toasts, popover], ([toasts, popover]) => {
    try {
      if (popover) {
        if (toasts.length > 0) {
          popover.showPopover();
        } else {
          popover.hidePopover();
        }
      }
    } catch (e) {
      console.log(e);
    }
  }, {
    immediate: true,
  });
</script>
<template>
  <Teleport :disabled="target === undefined" :to="target" defer>
    <div ref="popover" class="toasts-popover" popover="manual">
      <TransitionGroup name="toast" tag="ul">
        <UiToast v-for="(toast, i) in toasts" :key="toast.id" :position="i" :toast="toast" />
      </TransitionGroup>
    </div>
  </Teleport>
</template>
<style lang="scss" scoped>
  .toasts-popover {
    display: contents;
    background: none;
    border: none;
  }

  .toast-wrapper {
    position: fixed;
    display: flex;
    padding: 6px;
    background-color: rgb(0 0 0 / 0.6);
    border: 1px solid rgba(2, 28, 55, 0.08);
    border-radius: 16px;
    backdrop-filter: blur(2px);
    list-style: none;
    left: 50%;
    transform: translateX(-50%);
    transition-property: opacity, bottom, transform;
    transition-duration: 200ms;
    transition-timing-function: linear;
    width: 90dvw;

    &:nth-child(1) {
      opacity: 1;
      bottom: 52px;
      z-index: 900005;

      &.danger, &.warning {
        bottom: 50%;
        transform: translateX(-50%) translateY(50%);

        :deep(.toast-container) {
          padding: 0.75rem 1rem;

          .toast {
            align-items: center;
          }

          .btn {
            padding: 0.75rem 2rem;
          }
        }
      }
    }

    &:nth-child(2) {
      position: fixed;
      opacity: 0.6;
      bottom: 40px;
      z-index: 900004;
      transform: translateX(-50%) scale(0.90);
    }

    &:nth-child(3) {
      position: fixed;
      opacity: 0.4;
      bottom: 30px;
      z-index: 900003;
      transform: translateX(-50%) scale(0.80);
    }

    &:nth-child(4) {
      position: fixed;
      opacity: 0.2;
      bottom: 22px;
      z-index: 900002;
      transform: translateX(-50%) scale(0.70);
    }

    &:nth-child(n+5) {
      position: fixed;
      opacity: 0;
      bottom: 16px;
      z-index: 900001;
      transform: translateX(-50%) scale(0.60);
    }

    &:nth-child(n+2) {
      pointer-events: none;

      :deep(.toast) {
        .icon, .message, .actions {
          opacity: 0.5;
        }

        .message {
          .content {
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 1;
            overflow: hidden;
          }
        }
      }
    }

    &:nth-child(n+3) {
      :deep(.toast) {
        .icon, .message, .actions {
          opacity: 0;
        }
      }
    }
  }

  .toast-enter-from {
    opacity: 0 !important;
    transform: translateX(-50%) translateY(20px);
  }

  .toast-leave-to {
    opacity: 0 !important;
    transform: translateX(-50%) translateY(-20px);
  }

  @media screen and (min-width: 768px) {
    .toast-wrapper {
      width: auto;
      min-width: 700px;
      max-width: 90dvw;

      &:nth-child(n+2) {
        :deep(.toast) {
          width: 700px;
        }
      }
    }
  }
</style>
